import { useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { BucketTwrFormula } from "constants/index";
import { twrFormulaLabel } from "./summaryFigures";

const OPTION_LABEL_SX = {
  mr: 0,
  "& .MuiFormControlLabel-label": {
    typography: "body2",
  },
};

/**
 * The Summary row's gear: which time-weighted return formula the cards show,
 * and the option-pairing formula the next Calculate runs with.
 *
 * Disabled for the whole of a run, so neither can change under one.
 */
const SummaryFormulaSettings = ({
  disabled,
  twrFormula,
  onTwrFormulaChange,
  optionPairingV2,
  onOptionPairingChange,
  periodStart,
}: {
  disabled: boolean;
  twrFormula: BucketTwrFormula;
  onTwrFormulaChange: (formula: BucketTwrFormula) => void;
  optionPairingV2: boolean;
  onOptionPairingChange: (enabled: boolean) => void;
  periodStart: string | undefined;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="Time-weighted return formula"
        sx={{ fontSize: 20 }}
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <SettingsIcon fontSize="inherit" />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <FormControl>
            <FormLabel
              id="twr-formula-label"
              sx={{ mb: 1, typography: "subtitle2" }}
            >
              Time-Weighted Return
            </FormLabel>
            <RadioGroup
              aria-labelledby="twr-formula-label"
              value={twrFormula}
              onChange={(event) => {
                // A pick closes the popover: the cards move at once.
                setAnchor(null);
                onTwrFormulaChange(event.target.value as BucketTwrFormula);
              }}
            >
              {Object.values(BucketTwrFormula).map((formula) => (
                <FormControlLabel
                  key={formula}
                  value={formula}
                  control={<Radio size="small" />}
                  label={twrFormulaLabel(formula, periodStart)}
                  sx={OPTION_LABEL_SX}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ mt: 2, display: "block" }}>
            <FormLabel
              id="option-pairing-label"
              sx={{
                mb: 0.5,
                typography: "subtitle2",
                display: "block",
              }}
            >
              Option Pairing
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={optionPairingV2}
                  onChange={(event) =>
                    onOptionPairingChange(event.target.checked)
                  }
                  inputProps={{
                    "aria-labelledby": "option-pairing-label",
                  }}
                />
              }
              label={
                optionPairingV2
                  ? "Fixed pairing (PUT expiry)"
                  : "Previous formula"
              }
              sx={OPTION_LABEL_SX}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Applies on the next Calculate.
            </Typography>
          </FormControl>
        </Box>
      </Popover>
    </>
  );
};

export default SummaryFormulaSettings;
